/**
 * EditorHistory: a bounded undo/redo command stack for the Scene Builder.
 *
 * Every mutating editor action (add, delete, transform drag, duplicate, and
 * later property/event/portal edits) pushes a `EditorCommand` with symmetric
 * `undo`/`redo` closures. A transform gizmo drag is one command — its `before`
 * snapshot is captured on `transform:start` and its `after` snapshot on
 * `transform:end`.
 *
 * The stack is capped (oldest entries dropped) and the redo branch is cleared
 * whenever a new command is pushed, matching conventional editor semantics.
 */

export interface EditorCommand {
    label: string;
    undo(): void;
    redo(): void;
}

const MAX_HISTORY = 200;

export class EditorHistory {
    private _undo: EditorCommand[] = [];
    private _redo: EditorCommand[] = [];

    push(cmd: EditorCommand): void {
        this._undo.push(cmd);
        if (this._undo.length > MAX_HISTORY) this._undo.shift();
        this._redo.length = 0;
    }

    undo(): boolean {
        const cmd = this._undo.pop();
        if (!cmd) return false;
        cmd.undo();
        this._redo.push(cmd);
        return true;
    }

    redo(): boolean {
        const cmd = this._redo.pop();
        if (!cmd) return false;
        cmd.redo();
        this._undo.push(cmd);
        return true;
    }

    clear(): void {
        this._undo.length = 0;
        this._redo.length = 0;
    }

    get canUndo(): boolean { return this._undo.length > 0; }
    get canRedo(): boolean { return this._redo.length > 0; }
}
